/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React from "react";
import { ScrollView, View } from "react-native";
import { ApolloError } from "@apollo/client";

import { skaleWebBorderGray, skaleWhite } from "config/UpduoColors";
import TrackerWidget from "./TrackerWidget";
import { LoadingDots, Text } from "UpduoLibrary";
import { LocationCategory } from "./types";
import SubLocationTableWidget from "./SubLocationTableWidget";
import useManagerDashboard from "./useManagerDashboard";
import ManagerInfoWidget from "./ManagerInfoWidget";
import TopicInformationWidget from "./TopicInformationWidget";
import SessionCompletionWidget from "components/UserDashboardRoute/SessionCompletionWidget";
import Breadcrumb from "components/ExploreBar/Breadcrumb";
import QuotaDashboardErrorView from "../QuotaDashboardErrorView";

import * as UpduoAnalytics from "config/UpduoAnalytics";

function getSubLocationType(
  location: LocationCategory
): LocationCategory | undefined {
  switch (location) {
    case "organization":
      return "region";
    case "region":
      return "district";
    case "district":
      return "store";
    case "store":
      return "user";
  }
}

interface ManagerDashboardViewProps {
  locationType: LocationCategory;
  locationId: string;
}

export default function ManagerDashboardView({
  locationType,
  locationId,
}: ManagerDashboardViewProps) {
  const _handleError = (e: ApolloError | string) => {
    UpduoAnalytics.logEvent("app-managerdashboard-error", {
      locationType,
      locationId,
      error: e,
    });
  };

  const {
    lastUpdated,
    location,
    loading,
    loadNextPage,
    hasNextPage,
    quotaEndDate,
    error,
  } = useManagerDashboard({
    locationType,
    locationId,
    onError: _handleError,
  });

  const subLocationType = getSubLocationType(locationType);

  const padding = 18;

  if (!lastUpdated) {
    return (
      <QuotaDashboardErrorView
        message={
          "Participation info has not been calculated for this organization."
        }
      />
    );
  }

  return (
    <View>
      <View style={{ minHeight: 28 }}>
        {location ? (
          <Breadcrumb
            category={locationType}
            exploreObject={{
              ...location?.parentLocations,
              id: location.id,
              name: location.name,
              firstName: location.firstName,
              lastName: location.lastName,
            }}
            url={"quota-progress"}
          />
        ) : null}
      </View>
      <ScrollView
        style={{
          flex: 3,
          height: 450,
          borderWidth: 1,
          borderColor: skaleWebBorderGray,
          backgroundColor: skaleWhite,
          padding: 20,
        }}
      >
        <View
          style={{
            flexDirection: "row",
          }}
        >
          {loading && !location ? (
            <View
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexGrow: 1,
              }}
            >
              <LoadingDots />
            </View>
          ) : error ? (
            <View
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexGrow: 1,
              }}
            >
              <Text>{error}</Text>
            </View>
          ) : (
            <>
              <View
                style={{
                  display: "flex",
                  flexDirection: "column",
                  width: 265,
                }}
              >
                <TrackerWidget
                  quotaTarget={location?.quotaTarget}
                  quotaProgress={location?.quotaProgress}
                  targetDate={quotaEndDate}
                  isOnTrack={location?.isOnTrack}
                  lastUpdated={lastUpdated}
                />
                <View style={{ height: padding }} />
                {/* {location && subLocationType ? (
                <TargetsWidget
                  location={location}
                  subLocationTypeName={subLocationType}
                />
              ) : null} */}
              </View>
              <View style={{ width: padding }} />
              <View
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                  flexShrink: 1,
                }}
              >
                {
                  // @ts-ignore
                  locationType === "user" ? (
                    <SessionCompletionWidget
                      userId={location?.id}
                      hideConnectButton={true}
                    />
                  ) : (
                    <SubLocationTableWidget
                      locations={location?.subLocations || []}
                      locationTypeName={subLocationType!}
                      loadNextPage={() => loadNextPage()}
                      loading={loading}
                      hasNextPage={hasNextPage}
                    />
                  )
                }
                <View style={{ height: padding }} />
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "flex-start",
                  }}
                >
                  <TopicInformationWidget />
                  {/organization|user/.exec(locationType) ? null : (
                    <>
                      <View style={{ width: padding }} />
                      <ManagerInfoWidget manager={location?.leader} />
                    </>
                  )}
                </View>
              </View>
            </>
          )}
        </View>
      </ScrollView>
    </View>
  );
}
